import { api } from './api';
import type { Agent, AgentConversation, AgentStatus, AgentToolChoice, AgentVersion, EvalTool, PaginatedAgentResponse } from '../types';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

// Required: agentCode, name, tools. appId is create-only.
export interface AgentCreateRequest {
  agentCode: string;
  name: string;
  description?: string | null;
  status?: AgentStatus;
  systemPrompt?: string | null;
  tools: EvalTool[];
  modelRef?: string | null;
  toolChoice?: AgentToolChoice | null;
  modelParams?: Record<string, unknown> | null;
  promptRef?: string | null;
  promptVersion?: number | null;
  conversation?: AgentConversation | null;
  tags?: string[] | null;
  appId?: string;
}

// Update mirrors create minus appId (immutable).
export type AgentUpdateRequest = Partial<Omit<AgentCreateRequest, 'appId'>>;

// BE /agents/list query params (snake_case, matches the FastAPI Query names).
export interface AgentListParams {
  page?: number;
  page_size?: number;
  search?: string;
  include_deleted?: boolean;
  status?: string;
  tag?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string;
  app_id?: string;
  include_global?: boolean; // also include core/global rows (Show core toggle)
}

/** One thing that references an agent — enough to name it and link to it. */
export interface AgentDependentItem { id: string | null; name: string; appId: string | null }
export interface AgentDependentGroup {
  type: 'eval_suite' | 'conversation';
  label: string; count: number; items: AgentDependentItem[];
}
export interface AgentDependents { agentId: string; agentCode: string | null; total: number; groups: AgentDependentGroup[] }

/** The agent's upstream refs, name-resolved. */
export interface AgentOverview {
  agentId: string; agentCode: string | null;
  model: { ref?: string | null; name?: string | null };
  prompt: { ref?: string | null; name?: string | null; version?: number | null };
  rag: { ragCode?: string | null; ragId?: string | null; name?: string | null; version?: number | null };
  conversationPrompts: { role: string; promptRef: string | null; promptName?: string | null }[];
  onlineEval: { enabled?: boolean | null; sampleRate?: number | null; metrics?: string[] | null;
    judgeModelRef?: string | null; judgeModelName?: string | null };
}

/** How the agent performs over a window — response time, feedback, conversations. */
export interface AgentActivity {
  agentId: string; agentCode: string | null; windowDays: number;
  responseTime: { turns: number; avgLatencyMs: number | null; p95LatencyMs: number | null; avgTtftMs: number | null; sampled: number };
  quality: { scoredTurns: number; avgEvalScore: number | null };
  feedback: { total: number; positive: number; negative: number; neutral: number;
    bySentiment: Record<string, number>; avgRatingNormalized: number | null };
  conversations: { total: number; recent: { id: string; title: string; messageCount?: number | null;
    lastMessageAt?: string | null; createdAt?: string | null }[] };
}

export const agentsService = {
  list: async (params?: AgentListParams): Promise<PaginatedAgentResponse> => {
    const response = await api.get<PaginatedAgentResponse>('/api/v1/agents/list', { params });
    return response.data;
  },

  /** Every saved definition, newest first. */
  listVersions: async (id: string): Promise<AgentVersion[]> =>
    (await api.get<ApiResponse<AgentVersion[]>>(`/api/v1/agents/versions/${id}`)).data.data,

  getById: async (id: string): Promise<Agent> => {
    const response = await api.get<ApiResponse<Agent>>(`/api/v1/agents/get/${id}`);
    return response.data.data;
  },

  /** Downstream dependents: eval suites and conversations that use this agent. */
  getDependents: async (id: string): Promise<AgentDependents> =>
    (await api.get<ApiResponse<AgentDependents>>(`/api/v1/agents/${id}/dependents`)).data.data,

  /** Name-resolved upstream refs (model, prompt, RAG, conversation prompts, judge). */
  getOverview: async (id: string): Promise<AgentOverview> =>
    (await api.get<ApiResponse<AgentOverview>>(`/api/v1/agents/${id}/overview`)).data.data,

  /** Response time, feedback sentiment, eval quality and recent conversations. */
  getActivity: async (id: string, days = 30): Promise<AgentActivity> =>
    (await api.get<ApiResponse<AgentActivity>>(`/api/v1/agents/${id}/activity`, { params: { days } })).data.data,

  create: async (data: AgentCreateRequest): Promise<Agent> => {
    const response = await api.post<ApiResponse<Agent>>('/api/v1/agents/create', data);
    return response.data.data;
  },

  update: async (id: string, data: AgentUpdateRequest): Promise<Agent> => {
    const response = await api.put<ApiResponse<Agent>>(`/api/v1/agents/update/${id}`, data);
    return response.data.data;
  },

  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/api/v1/agents/delete/${id}`);
    return response.data.data;
  },

  restore: async (id: string): Promise<{ message: string }> => {
    const response = await api.patch<ApiResponse<{ message: string }>>(`/api/v1/agents/restore/${id}`);
    return response.data.data;
  },
};
